import chroma from "chroma-js";
import React from "react";
import { DEFAULT_GRID_RESOLUTION, GRID_SEAM } from "../../consts";
import { colorValue } from "../../css/color";
import { useUserInfo } from "../../liveblocks/maps";
import { styled } from "../../stitches.config";
import { Preview, User } from "../../types";
import { gridToPixelPosition, gridToPixelSize } from "../../utils/gridPosition";
import { deriveCreateType } from "./utils";

export default function PreviewRect({
  preview,
  userId,
}: {
  preview: Preview;
  userId?: User["id"];
}) {
  const [user] = useUserInfo(userId);
  const rectType = deriveRectType(preview);
  const position = gridToPixelPosition(preview.position);
  const size = gridToPixelSize(preview.size);
  const seamStyle = {
    transform: `translate(${position.x}px, ${position.y}px)`,
    width: `${size.width}px`,
    height: `${size.height}px`,
  };

  let style;
  let backgroundPatternStyle;
  switch (rectType) {
    case "text-rect":
      style = {
        backgroundColor: colorValue(user?.color, 95, 0.5),
        borderColor: colorValue(user?.color, 95, 0.5),
      };
      backgroundPatternStyle = {
        backgroundImage: `url("/static/textpattern.svg")`,
      };
      break;
    case "create-menu-rect":
      style = {
        backgroundColor: colorValue(user?.color, 95, 0.5),
        borderColor: colorValue(user?.color, 95, 0.5),
      };
      backgroundPatternStyle = {
        backgroundImage: `url("/static/pluspattern.svg")`,
      };
      break;
    case "selection-rect":
      style = {
        backgroundColor: colorValue(user?.color, 80, 0.05),
        borderColor: colorValue(user?.color, 80),
      };
      backgroundPatternStyle = {
        display: "none",
      };
      break;
    case "grey-rect":
      style = {
        backgroundColor: colorValue("paper", 60, 0.1),
        // use transparent as opposed to bg color because browser
        // seem to have a weird quirk that make border color darker
        // than it should
        // borderColor: colorValue("paper", 60, 0.1),
        borderColor: "transparent",
      };
      backgroundPatternStyle = {
        display: "none",
      };
      break;
  }

  return (
    <Seam style={seamStyle}>
      <Preview style={style}></Preview>
      <PreviewBackgroundPattern style={backgroundPatternStyle} />
    </Seam>
  );
}

type RectType =
  | "text-rect"
  | "selection-rect"
  | "create-menu-rect"
  | "grey-rect";

function deriveRectType(preview: Preview): RectType {
  if (preview.select) {
    return "selection-rect";
  }

  const createType = deriveCreateType(preview.size);

  switch (createType) {
    case "text-block":
      return "text-rect";
    case "no-block":
      return "grey-rect";
    case "create-menu-block":
      return "create-menu-rect";
  }
}

const Seam = styled("div", {
  padding: GRID_SEAM / 2,
  display: "block",
  willChange: "transform",
  position: "absolute",
  top: 0,
  left: 0,
  zIndex: "$blockPreview",
});

const Preview = styled("div", {
  height: "100%",
  width: "100%",
  pointerEvents: "none",
  borderWidth: "1px",
  borderStyle: "solid",
  borderRadius: "1px",
  transition: "background-color 0.01s backgroundColor 0.01s",
  elevation: 1,
  overflow: "hidden",
});

// Pattern offset keeps pattern from being clipped by the border
const patternOffset = 2;
const PreviewBackgroundPattern = styled("div", {
  position: "absolute",
  top: GRID_SEAM + patternOffset,
  left: GRID_SEAM + patternOffset,
  height: `calc(100% - ${GRID_SEAM * 2 + patternOffset * 2}px)`,
  width: `calc(100% - ${GRID_SEAM * 2 + patternOffset * 2}px)`,
  pointerEvents: "none",
  backgroundPosition: `${patternOffset}px ${patternOffset}px`,
  backgroundSize: `${DEFAULT_GRID_RESOLUTION}px ${DEFAULT_GRID_RESOLUTION}px`,
  backgroundRepeat: "repeat",
});
